import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { Archive, ExternalLink, FileText, Scale, ShieldCheck } from "lucide-react";

interface LegalVersion {
  version: string;
  stand: string;
  status: "aktuell" | "archiviert";
  archivedAt?: string;
  file?: string;
  changes: string;
}

interface LegalDoc {
  id: string;
  title: string;
  route: string;
  icon: typeof FileText;
  versions: LegalVersion[];
}

const documents: LegalDoc[] = [
  {
    id: "impressum",
    title: "Impressum",
    route: "/impressum",
    icon: FileText,
    versions: [
      {
        version: "2.0",
        stand: "August 2026",
        status: "aktuell",
        changes:
          "Versionierung und Standangabe ergänzt, Firmen-/Vertretungsdaten und Haftungsausschluss überprüft (ECG, UGB, MedTech-Hinweise).",
      },
      {
        version: "1.0",
        stand: "Mai 2024",
        status: "archiviert",
        archivedAt: "23.08.2026",
        file: "/rechtstexte-archiv/impressum-2024-05.html",
        changes: "Erstfassung der Website EAP® Abutments.",
      },
    ],
  },
  {
    id: "datenschutz",
    title: "Datenschutzerklärung",
    route: "/datenschutz",
    icon: ShieldCheck,
    versions: [
      {
        version: "3.0",
        stand: "August 2026",
        status: "aktuell",
        changes:
          "Cookie-Consent (Art. 6 Abs. 1 lit. a DSGVO / § 25 TDDDG), Download-Tracking mit pseudonymer Kennung inkl. dauerhaftem Opt-out, B2B-Klauseln, Anbieter-/Tool-Tabelle mit Drittlandgrundlagen, Speicherdauer-Tabelle, Abschnitt für DSGVO-Anfragen.",
      },
      {
        version: "1.0",
        stand: "Mai 2024",
        status: "archiviert",
        archivedAt: "23.08.2026",
        file: "/rechtstexte-archiv/datenschutz-2024-05.html",
        changes: "Fassung vor Einführung von Consent-Management und Download-Attribution.",
      },
    ],
  },
  {
    id: "agb",
    title: "Allgemeine Geschäftsbedingungen",
    route: "/agb",
    icon: Scale,
    versions: [
      {
        version: "2.0",
        stand: "August 2026",
        status: "aktuell",
        changes:
          "Standangabe und Versionierung ergänzt, Geltung für B2B (Zahnärzte/Zahntechniker), Gerichtsstand und Erfüllungsort bestätigt.",
      },
      {
        version: "1.0",
        stand: "Mai 2024",
        status: "archiviert",
        archivedAt: "23.08.2026",
        file: "/rechtstexte-archiv/agb-2024-05.html",
        changes: "Erstfassung der AGB.",
      },
    ],
  },
];

const AdminRechtstexte = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="pt-8 pb-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <Breadcrumbs items={[{ label: "Admin", href: "/admin" }, { label: "Rechtstexte & Archiv" }]} />

          <div className="flex items-start gap-3 mt-4 mb-6">
            <Archive className="h-8 w-8 text-primary shrink-0" />
            <div>
              <h1 className="break-words text-2xl sm:text-3xl font-bold text-foreground">Rechtstexte & Versionsarchiv</h1>
              <p className="text-muted-foreground mt-1">
                Aktuelle Fassungen (Stand August 2026) und archivierte Vorversionen – revisionssicher dokumentiert.
              </p>
            </div>
          </div>

          <Alert className="mb-8">
            <AlertDescription className="text-sm">
              Archivierte Fassungen sind als unveränderte HTML-Snapshots gespeichert und mit
              <code className="mx-1 px-1 rounded bg-muted">noindex</code>
              gekennzeichnet, damit sie nicht in Suchmaschinen erscheinen. Aufbewahrung: mindestens 7 Jahre
              (Nachweispflicht gem. § 132 BAO und Art. 5 Abs. 2 DSGVO – Rechenschaftspflicht).
            </AlertDescription>
          </Alert>

          <div className="space-y-6">
            {documents.map((doc) => {
              const Icon = doc.icon;
              return (
                <Card key={doc.id} className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-primary" />
                      <h2 className="text-xl font-semibold text-foreground">{doc.title}</h2>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={doc.route}>
                        Aktuelle Fassung ansehen
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>

                  <ul className="space-y-4">
                    {doc.versions.map((v) => (
                      <li
                        key={v.version}
                        className="rounded-lg border border-border p-4 bg-muted/30"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-semibold text-foreground">Version {v.version}</span>
                          <span className="text-sm text-muted-foreground">Stand {v.stand}</span>
                          <Badge variant={v.status === "aktuell" ? "default" : "secondary"}>
                            {v.status === "aktuell" ? "aktuell gültig" : "archiviert"}
                          </Badge>
                          {v.archivedAt && (
                            <span className="text-xs text-muted-foreground">
                              archiviert am {v.archivedAt}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{v.changes}</p>
                        {v.file && (
                          <Button variant="ghost" size="sm" className="mt-3 px-0" asChild>
                            <a href={v.file} target="_blank" rel="noopener noreferrer">
                              Archivfassung öffnen
                              <ExternalLink className="ml-2 h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <Card className="p-6 mt-8">
            <h2 className="text-lg font-semibold mb-3">Vorgehen bei künftigen Änderungen</h2>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground">
              <li>Aktuelle Fassung als HTML-Snapshot unter <code className="px-1 rounded bg-muted">/rechtstexte-archiv/</code> ablegen (Dateiname: <code className="px-1 rounded bg-muted">dokument-JJJJ-MM.html</code>).</li>
              <li>Neuen Text auf der öffentlichen Seite einpflegen und Standangabe sowie Versionsnummer erhöhen.</li>
              <li>Version hier in der Übersicht eintragen und die Änderungen kurz beschreiben.</li>
              <li>Änderung zusätzlich im <Link to="/admin/changelog" className="text-primary hover:underline">Changelog</Link> vermerken.</li>
            </ol>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminRechtstexte;
